// Location Edit/Create/Delete Module
// Handles CRUD operations for locations

import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { t } from '../core/i18n.js';
import { showError, showSuccess } from '../core/ui.js';
import { locationsInfiniteScroll, loadLocationsData } from './locations.js';
import { initAccountSearch } from '../utils/account-search.js';
import { getCurrentUser } from '../utils/helpers.js';

// Helper function for alarm status icons
function getAlarmStatusIconForSensor(sensor) {
    const alarmInfo = sensor.alarm_status || {};
    const hasAlarms = alarmInfo.total_alarms > 0;
    
    if (!hasAlarms) {
        return ''; // No icon if no alarms
    }
    
    // Determine color and title based on status
    let colorClass, title;
    
    if (alarmInfo.suspended_count > 0) {
        // Yellow if any alarm is suspended
        colorClass = 'text-yellow-500';
        title = `${alarmInfo.suspended_count} alarm(s) suspended`;
    } else if (alarmInfo.active_count > 0) {
        // Green if has active alarms
        colorClass = 'text-green-500';
        title = `${alarmInfo.active_count} active alarm(s)`;
    } else {
        // Gray if all alarms are disabled
        colorClass = 'text-gray-400';
        title = `${alarmInfo.total_alarms} alarm(s) disabled`;
    }
    
    // Bell icon (outline style) - smaller for inline use
    return `
        <svg class="w-4 h-4 ${colorClass} mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="${title}">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    `;
}

// Close modal helper
function closeModal() {
    const modalContainer = document.getElementById('modal');
    if (modalContainer) {
        modalContainer.innerHTML = '';
        modalContainer.classList.add('hidden');
    }
}

// Edit Location - Now uses unified modal from locations-modals.js
export async function editLocation(locationId) {
    // Import and call the unified modal function
    const { showLocationModal } = await import('./locations-modals.js');
    showLocationModal(locationId);
    return; // Exit early - unified modal handles everything
    
    // Old code below kept for reference but never executed
    try {
        // Fetch location details
        const response = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
            headers: {
                'Authorization': `Bearer ${auth.token}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load location details');
        }

        const location = await response.json();
        console.log('Loaded location data for editing:', location);

        const modalHtml = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div class="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                        <h3 class="text-lg font-medium text-gray-900">${t('locations.edit')}</h3>
                    </div>
                    <form onsubmit="handleUpdateLocation(event, '${locationId}')" class="px-6 py-4 space-y-4">
                        <!-- Name and Account - Two Column -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Name -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    ${t('locations.name')} <span class="text-red-500">*</span>
                                </label>
                                <input type="text" 
                                       id="edit-location-name" 
                                       value="${location.name}"
                                       required
                                       class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                            </div>

                            <!-- Account Tree -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    ${t('locations.selectAccount')} <span class="text-red-500">*</span>
                                </label>
                                <!-- Search Field -->
                                <div class="relative mb-2">
                                    <input type="text" 
                                           id="edit-location-account-search-main" 
                                           placeholder="Search accounts by name..." 
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                    <!-- Search Results Dropdown -->
                                    <div id="edit-location-account-search-results-main" 
                                         class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                    </div>
                                </div>
                                <!-- Account Tree -->
                                <div id="edit-location-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                    <div class="text-gray-400 text-sm">${t('common.loading')}</div>
                                </div>
                                <input type="hidden" id="edit-location-account" name="account_id" value="${location.account_id}" required>
                            </div>
                        </div>

                        <!-- Address and Coordinates - Two Column -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Address -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    ${t('locations.address')}
                                </label>
                                <textarea id="edit-location-address" 
                                          rows="3"
                                          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">${location.address || ''}</textarea>
                                <button type="button" 
                                        onclick="geocodeLocationAddress('${locationId}')"
                                        class="mt-2 inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors">
                                    <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Geocode Address
                                </button>
                            </div>

                            <!-- Coordinates -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Coordinates
                                </label>
                                <div class="space-y-2">
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">Latitude</label>
                                        <input type="number" 
                                               id="edit-location-latitude" 
                                               value="${location.latitude || ''}"
                                               step="0.000001"
                                               placeholder="e.g., 56.26392"
                                               class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500">
                                    </div>
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">Longitude</label>
                                        <input type="number" 
                                               id="edit-location-longitude" 
                                               value="${location.longitude || ''}"
                                               step="0.000001"
                                               placeholder="e.g., 9.501785"
                                               class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500">
                                    </div>
                                </div>
                                <p class="mt-1 text-xs text-gray-500">Auto-filled when geocoding address</p>
                            </div>
                        </div>

                        <!-- Sensors -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                ${t('locations.sensors')}
                            </label>
                            <input type="text"
                                   id="edit-location-sensor-search"
                                   placeholder="${t('common.search')} (ID / sensor)"
                                   oninput="filterLocationSensorList(this, 'edit-location-sensors-container')"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 text-sm mb-2">
                            <div id="edit-location-sensors-container" class="border border-gray-300 rounded-md p-3 max-h-[300px] overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('common.loading')}</div>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">${t('locations.sensorsHelp')}</p>
                        </div>

                        <!-- External Users -->
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                External Users (Optional)
                            </label>
                            <div id="edit-location-contacts-container" class="border border-gray-300 rounded-md p-3 max-h-[200px] overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('common.loading')}</div>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">Select external contact users who should be listed for this location</p>
                        </div>

                        <!-- Time Budget -->
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-3">
                                ${t('locations.timeBudgetOptional')}
                            </label>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Budget Period -->
                                <div>
                                    <label class="block text-sm text-gray-600 mb-2">${t('locations.timeBudgetPeriod')}</label>
                                    <div class="space-y-2">
                                        <label class="flex items-center">
                                            <input type="radio" 
                                                   name="edit-time-budget-period" 
                                                   value="weekly" 
                                                   ${location.time_budget_period === 'monthly' ? '' : 'checked'}
                                                   class="text-primary-600 focus:ring-primary-500">
                                            <span class="ml-2 text-sm text-gray-700">${t('locations.weekly')}</span>
                                        </label>
                                        <label class="flex items-center">
                                            <input type="radio" 
                                                   name="edit-time-budget-period" 
                                                   value="monthly" 
                                                   ${location.time_budget_period === 'monthly' ? 'checked' : ''}
                                                   class="text-primary-600 focus:ring-primary-500">
                                            <span class="ml-2 text-sm text-gray-700">${t('locations.monthly')}</span>
                                        </label>
                                    </div>
                                </div>

                                <!-- Budget Hours -->
                                <div>
                                    <label class="block text-sm text-gray-600 mb-2">${t('locations.timeBudgetHoursOptional')}</label>
                                    <input type="number" 
                                           id="edit-location-budget-hours" 
                                           value="${location.time_budget_hours || ''}"
                                           min="0" 
                                           step="0.5"
                                           class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                                           placeholder="e.g., 40">
                                </div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button type="button" 
                                    onclick="closeEditLocationModal()"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                ${t('common.cancel')}
                            </button>
                            <button type="submit"
                                    class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                                ${t('common.update')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        `;

        const modalContainer = document.getElementById('modal');
        if (modalContainer) {
            modalContainer.innerHTML = modalHtml;
            modalContainer.classList.remove('hidden');
        }

        // Load accounts tree with pre-selected account
        await loadAccountsForLocationModal('edit-location-account-tree-container', 'edit-location-account', location.account_id);
        
        // Load available sensors with current location's sensors selected
        await loadAvailableSensorsForLocation('edit-location-sensors-container', locationId);
        
        // Load available external users for contact selection
        await loadAvailableContactUsers('edit-location-contacts-container', location.contact_user_ids || [], location.account_id);

    } catch (error) {
        showError('Failed to load location: ' + error.message);
        console.error('Error loading location:', error);
    }
}

// Load available sensors for location association
export async function loadAvailableSensorsForLocation(containerId, currentLocationId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    try {
        container.innerHTML = '<p class="text-sm text-gray-500">Loading sensors...</p>';
        
        const pageSize = 500;
        let allSensors = [];
        let skip = 0;
        let hasMore = true;
        
        while (hasMore) {
            const params = new URLSearchParams();
            params.append('limit', pageSize.toString());
            params.append('skip', skip.toString());
            
            if (!currentLocationId) {
                params.append('unassociated_only', 'true');
            }
            
            const response = await fetch(`/api/sensors?${params}`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to load sensors: ${response.status}`);
            }
            
            const data = await response.json();
            const sensors = data.sensors || [];
            
            allSensors = allSensors.concat(sensors);
            
            if (sensors.length < pageSize || allSensors.length >= data.total) {
                hasMore = false;
            } else {
                skip += pageSize;
                container.innerHTML = `<p class="text-sm text-gray-500">Loading sensors... (${allSensors.length} of ${data.total})</p>`;
            }
        }
        
        let availableSensors;
        if (currentLocationId) {
            availableSensors = allSensors.filter(sensor => {
                const isUnassociated = !sensor.location_id && !sensor.asset_id;
                const isAssociatedWithThisLocation = sensor.location_id === currentLocationId;
                return isUnassociated || isAssociatedWithThisLocation;
            });
        } else {
            availableSensors = allSensors;
        }
        
        const assignedSensors = availableSensors.filter(sensor => 
            currentLocationId && sensor.location_id === currentLocationId
        );
        const unassignedSensors = availableSensors.filter(sensor => 
            !sensor.location_id && !sensor.asset_id
        );
        
        let html = '';
        
        if (currentLocationId && assignedSensors.length > 0) {
            html += `
                <div class="mb-4 pb-4 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-2">
                        <h4 class="text-sm font-medium text-gray-900">Currently Assigned (${assignedSensors.length})</h4>
                        <button type="button" 
                                onclick="document.querySelectorAll('.assigned-sensor-checkbox').forEach(cb => cb.checked = false)"
                                class="text-xs text-red-600 hover:text-red-800">
                            Deselect All
                        </button>
                    </div>
                    <div class="bg-blue-50 rounded-md p-2 space-y-1">
                        ${assignedSensors.map(sensor => {
                            const alarmIcon = getAlarmStatusIconForSensor(sensor);
                            const searchText = `${sensor.sensor_id || ''} ${sensor.name || ''}`.toLowerCase().replace(/"/g, '&quot;');
                            return `
                                <div class="flex items-center py-1" data-sensor-search="${searchText}">
                                    <input type="checkbox" 
                                           id="sensor-${sensor.id}" 
                                           value="${sensor.id}"
                                           checked
                                           class="location-sensor-checkbox assigned-sensor-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <label for="sensor-${sensor.id}" class="ml-2 text-sm text-gray-700 cursor-pointer flex-grow flex items-center">
                                        <code class="bg-white px-1 py-0.5 rounded text-xs mr-1">${sensor.sensor_id}</code>
                                        ${alarmIcon}
                                        <span>${sensor.name}</span>
                                    </label>
                                    <button type="button"
                                            onclick="showEditSensorModal('${sensor.id}', '${currentLocationId}')"
                                            class="ml-2 px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded hover:bg-primary-200"
                                            title="Manage alarms for this sensor">
                                        Edit Alarm
                                    </button>
                                </div>
                            `;
                        }).join('')}
                    </div>
                </div>
            `;
        }
        
        if (unassignedSensors.length > 0) {
            html += `
                <div>
                    <h4 class="text-sm font-medium text-gray-700 mb-2">
                        Available Sensors (${unassignedSensors.length})
                    </h4>
                    <div class="space-y-1">
                        ${unassignedSensors.map(sensor => {
                            const alarmIcon = getAlarmStatusIconForSensor(sensor);
                            const searchText = `${sensor.sensor_id || ''} ${sensor.name || ''}`.toLowerCase().replace(/"/g, '&quot;');
                            return `
                                <div class="flex items-center py-1" data-sensor-search="${searchText}">
                                    <input type="checkbox" 
                                           id="sensor-${sensor.id}" 
                                           value="${sensor.id}"
                                           class="location-sensor-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <label for="sensor-${sensor.id}" class="ml-2 text-sm text-gray-700 cursor-pointer flex-grow flex items-center">
                                        <code class="bg-gray-100 px-1 py-0.5 rounded text-xs mr-1">${sensor.sensor_id}</code>
                                        ${alarmIcon}
                                        <span>${sensor.name}</span>
                                    </label>
                                </div>
                            `;
                        }).join('')}
                    </div>
                </div>
            `;
        } else if (assignedSensors.length === 0) {
            html = '<p class="text-sm text-gray-500">No available sensors to associate</p>';
        }
        
        container.innerHTML = html;

        // Re-apply current search filter after re-render
        const searchInput = document.getElementById('edit-location-sensor-search');
        if (searchInput && searchInput.value && typeof window.filterLocationSensorList === 'function') {
            window.filterLocationSensorList(searchInput, containerId);
        }
        
    } catch (error) {
        console.error('Error loading available sensors:', error);
        container.innerHTML = `<p class="text-sm text-red-500">Failed to load sensors: ${error.message}</p>`;
    }
}

// Load available external contact users for location
export async function loadAvailableContactUsers(containerId, selectedContactIds = [], locationAccountId = null) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    try {
        container.innerHTML = '<p class="text-sm text-gray-500">Loading users...</p>';
        
        // Get the account to query - use location's account if provided, otherwise current user's account
        const currentUser = getCurrentUser();
        const accountId = locationAccountId || currentUser?.account_id;
        
        if (!accountId) {
            container.innerHTML = '<p class="text-sm text-gray-500">Unable to load users</p>';
            return;
        }

        const response = await fetch(`${API_BASE_URL}/users?account_id=${accountId}`, {
            headers: {
                'Authorization': `Bearer ${auth.token}`
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to load users: ${response.status}`);
        }

        const data = await response.json();
        const allUsers = data.users || [];
        
        // Filter to only show External contact users
        const externalUsers = allUsers.filter(user => user.role === 'External contact');
        
        if (externalUsers.length === 0) {
            container.innerHTML = '<p class="text-sm text-gray-500">No external contact users available in this account</p>';
            return;
        }
        
        // Sort users by name
        externalUsers.sort((a, b) => {
            const nameA = a.full_name || a.name || a.username || '';
            const nameB = b.full_name || b.name || b.username || '';
            return nameA.localeCompare(nameB);
        });
        
        let html = '<div class="space-y-2">';
        
        externalUsers.forEach(user => {
            const userId = user._id;
            const userName = user.full_name || user.name || user.username;
            const isSelected = selectedContactIds.includes(userId);
            
            html += `
                <div class="flex items-center">
                    <input type="checkbox" 
                           id="contact-user-${userId}" 
                           value="${userId}"
                           ${isSelected ? 'checked' : ''}
                           class="location-contact-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                    <label for="contact-user-${userId}" class="ml-2 text-sm text-gray-700 cursor-pointer flex-grow">
                        <span class="font-medium">${userName}</span>
                    </label>
                </div>
            `;
        });
        
        html += '</div>';
        
        container.innerHTML = html;
        
    } catch (error) {
        console.error('Error loading external users:', error);
        container.innerHTML = `<p class="text-sm text-red-500">Failed to load users: ${error.message}</p>`;
    }
}

// Handle location update
export async function handleUpdateLocation(event, locationId) {
    event.preventDefault();

    const name = document.getElementById('edit-location-name').value.trim();
    const accountId = document.getElementById('edit-location-account').value;
    const address = document.getElementById('edit-location-address').value.trim();
    const latitude = document.getElementById('edit-location-latitude').value.trim();
    const longitude = document.getElementById('edit-location-longitude').value.trim();
    
    const selectedSensors = Array.from(document.querySelectorAll('.location-sensor-checkbox:checked'))
        .map(cb => cb.value);

    const selectedContactUsers = Array.from(document.querySelectorAll('.location-contact-checkbox:checked'))
        .map(cb => cb.value);

    const budgetPeriodElement = document.querySelector('input[name="edit-time-budget-period"]:checked');
    const budgetHoursElement = document.getElementById('edit-location-budget-hours');
    const budgetPeriod = budgetPeriodElement ? budgetPeriodElement.value : null;
    const budgetHoursValue = budgetHoursElement.value.trim();
    const budgetHours = budgetHoursValue ? parseFloat(budgetHoursValue) : null;

    if (!name || !accountId) {
        showError(t('locations.requiredFields'));
        return;
    }

    try {
        const requestBody = {
            name,
            account_id: accountId,
            address: address || null,
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
            sensor_ids: selectedSensors,
            contact_user_ids: selectedContactUsers
        };

        if (budgetPeriod) {
            requestBody.time_budget_period = budgetPeriod;
        }
        if (budgetHours !== null && !isNaN(budgetHours)) {
            requestBody.time_budget_hours = budgetHours;
        }

        const response = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || t('locations.updateError'));
        }

        showSuccess(t('locations.updateSuccess'));
        await closeEditLocationModal();

    } catch (error) {
        showError(t('locations.updateError') + ': ' + error.message);
        console.error('Error updating location:', error);
    }
}

// Close edit location modal
export async function closeEditLocationModal() {
    closeModal();
    
    if (typeof loadLocationsData === 'function') {
        locationsInfiniteScroll.currentPage = 1;
        locationsInfiniteScroll.allLocations = [];
        locationsInfiniteScroll.hasMore = true;
        await loadLocationsData(true);
    }
}

// Load account tree for location modals
async function loadAccountsForLocationModal(containerId, hiddenInputId, selectedAccountId = null) {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const container = document.getElementById(containerId);
        if (!container) return;
        
        if (response && response.accounts_tree) {
            container.innerHTML = renderLocationAccountTree(response.accounts_tree, selectedAccountId);
            
            if (selectedAccountId) {
                const radio = container.querySelector(`input[value="${selectedAccountId}"]`);
                if (radio) {
                    radio.checked = true;
                }
            }
            
            // Initialize account search if this is the edit location modal
            if (containerId === 'edit-location-account-tree-container') {
                initAccountSearch(
                    'edit-location-account-search-main',
                    'edit-location-account-search-results-main',
                    'edit-location-account-tree-container',
                    'edit-location-account'
                );
            }
        } else {
            container.innerHTML = '<div class="text-gray-500 text-sm">No accounts found</div>';
        }
            
    } catch (error) {
        console.error('Error loading accounts:', error);
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = '<div class="text-red-500 text-sm">Error loading accounts</div>';
        }
    }
}

// Render account tree for location modals
function renderLocationAccountTree(accounts, selectedAccountId = null, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isSelected = accountId === selectedAccountId;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleLocationAccountTreeItem('location-tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="location-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform ${isSelected ? 'rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="location-account-selection" value="${accountId}" 
                               onchange="selectLocationAccount('${accountId}')"
                               ${isSelected ? 'checked' : ''}
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="location-tree-${accountId}" class="${isSelected ? '' : 'hidden'}">
                        ${renderLocationAccountTree(account.children, selectedAccountId, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

// Toggle account tree item
export function toggleLocationAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('location-tree-', 'location-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

// Select account
export function selectLocationAccount(accountId) {
    const createInput = document.getElementById('create-location-account');
    if (createInput) {
        createInput.value = accountId;
    }
    
    const editInput = document.getElementById('edit-location-account');
    if (editInput) {
        editInput.value = accountId;
    }
}

// Geocode address (stub - requires Google Maps API)
export async function geocodeLocationAddress(locationId) {
    showError('Geocoding feature requires Google Maps API configuration');
}

export default {
    editLocation,
    handleUpdateLocation,
    closeEditLocationModal,
    toggleLocationAccountTreeItem,
    selectLocationAccount,
    geocodeLocationAddress,
    loadAvailableSensorsForLocation
};

// Export to window for global access (needed by sensor-edit.js and modals)
window.loadAvailableSensorsForLocation = loadAvailableSensorsForLocation;
window.geocodeLocationAddress = geocodeLocationAddress;
